import { useState, useEffect, useCallback } from 'react';
import { FiTrash2, FiCheck } from 'react-icons/fi';
import { apiGetAllMessages, apiUpdateMessageStatus, apiDeleteMessage } from '../api';
import MessageDetailPanel from './messages/MessageDetailPanel';
import Pagination from '../components/Pagination';
import { confirmAdminAction } from '../utils/adminConfirm';

const MessagesManager = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGetAllMessages(page, 20, filterStatus);
      setMessages(data.messages || []);
      setPagination(data.pagination || { total: 0, pages: 1 });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, filterStatus]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await apiUpdateMessageStatus(id, status);
      if (filterStatus && filterStatus !== status) {
        await load();
      } else {
        setMessages((current) => current.map((m) => (m.id === id ? { ...m, status } : m)));
      }
      if (selectedMessage?.id === id) {
        setSelectedMessage({ ...selectedMessage, status });
      }
    } catch { /* API client displays the error toast. */ }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmAdminAction('This message will be permanently removed.', { title: 'Delete message', confirmLabel: 'Delete' });
    if (!confirmed) return;
    try {
      await apiDeleteMessage(id);
      await load();
      if (selectedMessage?.id === id) setSelectedMessage(null);
    } catch { /* API client displays the error toast. */ }
  };

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
        <p className="text-black mt-4">Loading messages...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <select
          value={filterStatus}
          onChange={(e) => {
            setFilterStatus(e.target.value);
            setPage(1);
          }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
        >
          <option value="">All Messages</option>
          <option value="new">New</option>
          <option value="read">Read</option>
          <option value="replied">Replied</option>
        </select>
        <span className="text-sm text-black">{pagination.total} messages</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-black sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left">From</th>
                  <th className="px-4 py-3 text-left">Subject</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Date</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {messages.map((msg) => (
                  <tr
                    key={msg.id}
                    className={`hover:bg-gray-50 cursor-pointer transition ${
                      selectedMessage?.id === msg.id ? 'bg-orange-50' : ''
                    } ${(msg.status || 'new') === 'new' ? 'font-medium' : ''}`}
                    onClick={() => {
                      setSelectedMessage(msg);
                      if ((msg.status || 'new') === 'new') updateStatus(msg.id, 'read');
                    }}
                  >
                    <td className="px-4 py-3">
                      <p className="text-black">{msg.name}</p>
                      <p className="text-xs text-black font-normal">{msg.email}</p>
                    </td>
                    <td className="px-4 py-3 text-black line-clamp-1 font-normal">
                      {msg.subject || 'No subject'}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          (msg.status || 'new') === 'new'
                            ? 'bg-yellow-100 text-yellow-700'
                            : msg.status === 'read'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-green-100 text-green-700'
                        }`}
                      >
                        {msg.status || 'new'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-black text-xs font-normal">
                      {new Date(msg.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right space-x-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          updateStatus(msg.id, 'replied');
                        }}
                        className="text-green-500 hover:text-green-700"
                        title="Mark Replied"
                      >
                        <FiCheck size={15} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(msg.id);
                        }}
                        className="text-red-500 hover:text-red-700"
                        title="Delete"
                      >
                        <FiTrash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
                {messages.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-black">
                      No messages
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <MessageDetailPanel
          selectedMessage={selectedMessage}
          onUpdateStatus={updateStatus}
          onDelete={handleDelete}
        />
      </div>
      <Pagination page={page} pages={pagination.pages} onChange={setPage} />
    </div>
  );
};

export default MessagesManager;
